/**
 * populateGetToKnow — creates the "Get to know To To-Do" tutorial project.
 *
 * Ported from v1's populateGetToKnow.ts (507 lines). Simplified for v2:
 * - Uses contentToDocPair to create Y.Doc + docHtml for tutorial instructions
 * - No Capacitor platform check (not integrated yet)
 * - All strings use i18next t() for translation
 * - Uses v2 createTaskDefaults() and db operations
 *
 * Creates:
 *   1 Project — "Get to know To To-Do"
 *   ~12 sections + tasks — tutorial content with rich text notes
 *   Sets #first-start setting → triggers FirstStartDialog
 *
 * @param id — optional: pass an ID to re-create from Settings > Help
 *             (skips setting #first-start so the welcome dialog doesn't reappear)
 */

import i18next from "i18next"
import { db } from "@/db/schema"
import { ObjectType, ObjectStatus } from "@/db/schema"
import { createTaskDefaults } from "@/shared/types/task"
import { now } from "@/shared/utils/date/dateUtils"
import { setTaskDoc, hasDocContext } from "@/db/operations"

export async function populateGetToKnow(id?: string): Promise<string> {
  const t = i18next.t.bind(i18next)
  const timestamp = now()
  const projectId = id ?? crypto.randomUUID()

  // -----------------------------------------------------------------------
  // 1. Create tutorial project
  // -----------------------------------------------------------------------

  const project = createTaskDefaults({
    id: projectId,
    type: ObjectType.Project,
    title: t("Get to know To To-Do"),
    parentPath: "root",
    index: 0,
    status: ObjectStatus.Unfinished,
    owner: "",
    realmId: "",
    createdAt: timestamp,
    createdBy: "system",
  })

  // -----------------------------------------------------------------------
  // 2. Build tutorial items
  // -----------------------------------------------------------------------

  const parentPath = `root/${projectId}`
  let idx = 0

  const items = [
    // --- Section: Learn the basics ---
    section(t("Learn the basics"), parentPath, idx++, timestamp),

    task(
      t("Click this to-do to see the details"),
      parentPath,
      idx++,
      timestamp,
      t(
        "Great job! You just opened your first to-do. This is where you can add notes, checklists, tags, and more. Try checking off this task when you're done!",
      ),
    ),
    task(
      t("Create a new to-do by pressing the + button"),
      parentPath,
      idx++,
      timestamp,
      t(
        "Tap the + button at the bottom of the screen to create a new to-do. You can also use the keyboard shortcut Ctrl+N (or Cmd+N on Mac).",
      ),
    ),
    task(
      t("Add this to-do to your Today list"),
      parentPath,
      idx++,
      timestamp,
      t(
        "Tap the star icon to add this to-do to your Today list. Tasks in Today appear in your daily view so you always know what to focus on.",
      ),
    ),
    task(
      t("Plan this to-do for later using the calendar"),
      parentPath,
      idx++,
      timestamp,
      t(
        "Tap the calendar icon to schedule this to-do for a specific date. Scheduled tasks move to the Upcoming view and appear in Today when the day arrives.",
      ),
    ),
    task(
      t("Create a new heading to organize your tasks"),
      parentPath,
      idx++,
      timestamp,
      t(
        "Headings help you group related tasks within a project. Use them to create sections like \"Research\", \"Design\", or \"Follow up\".",
      ),
    ),
    task(
      t("Create a list or project from the sidebar"),
      parentPath,
      idx++,
      timestamp,
      t(
        "Projects help you organize larger efforts with multiple steps. Tap the + button in the sidebar to create a new project or list.",
      ),
    ),

    // --- Section: Boost your productivity ---
    section(t("Boost your productivity"), parentPath, idx++, timestamp),

    task(
      t("Tag your to-dos for easy filtering"),
      parentPath,
      idx++,
      timestamp,
      t(
        "Tags let you categorize tasks across different projects. Add a tag by using the tag button in the task details. Filter by tag from the sidebar.",
      ),
    ),
    task(
      t("Go step by step with checklists"),
      parentPath,
      idx++,
      timestamp,
      t(
        "Break down complex tasks into smaller steps using checklists. Open a to-do and tap the checklist button to add step-by-step items.",
      ),
    ),
    task(
      t("Convert a to-do into a project"),
      parentPath,
      idx++,
      timestamp,
      t(
        "When a to-do grows too complex for a single task, convert it into a project. Open the task menu and select \"Convert to Project\" to get started.",
      ),
    ),

    // --- Section: Before you go ---
    section(t("Before you go…"), parentPath, idx++, timestamp),

    task(
      t("Get To To-Do for other devices"),
      parentPath,
      idx++,
      timestamp,
      t(
        "To To-Do syncs across all your devices. Download it on iOS and Android, or use the web app. Your data stays in sync automatically.",
      ),
    ),
    task(
      t("Any questions? Contact support@totodo.app"),
      parentPath,
      idx++,
      timestamp,
      t(
        "We're here to help! Send us an email at support@totodo.app and we'll get back to you as soon as possible.",
      ),
    ),
  ]

  // -----------------------------------------------------------------------
  // 3. Write to database in a single transaction
  // -----------------------------------------------------------------------

  await db.transaction("rw", db.totodo, db.setting, async () => {
    await db.totodo.add(project)
    await db.totodo.bulkAdd(items.map((item) => item.obj))

    // Only set #first-start on initial creation (not re-creation from Help)
    if (!id) {
      await db.setting.add({
        id: "#first-start",
        value: projectId,
        device: "system",
        createdAt: timestamp,
        createdBy: "system",
      })
    }
  })

  // -----------------------------------------------------------------------
  // 4. Add rich text doc content to tutorial tasks (after creation)
  // -----------------------------------------------------------------------
  // setTaskDoc writes to $totodo.doc_updates which requires its own
  // transaction, so we do it after the initial bulk add.

  if (hasDocContext()) {
    const docItems = items.filter((item) => item.docContent)
    for (const item of docItems) {
      await setTaskDoc(item.obj.id!, item.docContent!, "text")
    }
  }

  return projectId
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface TutorialItem {
  obj: ReturnType<typeof createTaskDefaults>
  docContent?: string
}

function section(
  title: string,
  parentPath: string,
  index: number,
  timestamp: string,
): TutorialItem {
  return {
    obj: createTaskDefaults({
      id: crypto.randomUUID(),
      type: ObjectType.Section,
      title,
      parentPath,
      index,
      status: ObjectStatus.Unfinished,
      createdAt: timestamp,
      createdBy: "system",
    }),
  }
}

function task(
  title: string,
  parentPath: string,
  index: number,
  timestamp: string,
  docContent?: string,
): TutorialItem {
  return {
    obj: createTaskDefaults({
      id: crypto.randomUUID(),
      type: ObjectType.Task,
      title,
      parentPath,
      index,
      status: ObjectStatus.Unfinished,
      createdAt: timestamp,
      createdBy: "system",
    }),
    docContent,
  }
}
